import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, XCircle, Zap } from "lucide-react";
import Navbar from "@/components/Navbar";

const SCHEMA = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": "ChatGPT vs Lakshmi: which AI tool writes LinkedIn posts in your voice?",
    "description": "Detailed comparison of ChatGPT vs Lakshmi for writing LinkedIn posts. Which AI tool actually sounds like you, scores quality, and is built for Indian professionals?",
    "url": "https://hilakshmi.com/blog/chatgpt-vs-lakshmi-linkedin-posts",
    "author": { "@type": "Organization", "name": "Lakshmi", "url": "https://hilakshmi.com" },
    "publisher": { "@type": "Organization", "name": "Lakshmi", "url": "https://hilakshmi.com" },
    "datePublished": "2026-02-05",
    "keywords": "ChatGPT vs Lakshmi, ChatGPT LinkedIn posts, AI LinkedIn post writer India, best AI tool LinkedIn posts",
    "image": "https://hilakshmi.com/image/og-cover.svg"
};

const COMPARISON = [
    { feature: "LinkedIn-specific hook structure", chatgpt: false, lakshmi: true },
    { feature: "Learns your writing voice", chatgpt: false, lakshmi: true },
    { feature: "Writes 4 platforms simultaneously", chatgpt: false, lakshmi: true },
    { feature: "Quality scored 0–100", chatgpt: false, lakshmi: true },
    { feature: "4-agent specialized pipeline", chatgpt: false, lakshmi: true },
    { feature: "Built for Indian professional context", chatgpt: false, lakshmi: true },
    { feature: "Retries automatically if quality is low", chatgpt: false, lakshmi: true },
    { feature: "General purpose writing", chatgpt: true, lakshmi: false },
    { feature: "Needs detailed prompting to get good output", chatgpt: true, lakshmi: false },
];

export default function BlogPost3() {
    useEffect(() => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.text = JSON.stringify(SCHEMA);
        document.head.appendChild(s);
        document.title = "ChatGPT vs Lakshmi: Which AI Writes Better LinkedIn Posts? — Lakshmi";
        const desc = document.querySelector('meta[name="description"]');
        if (desc) desc.setAttribute("content", "ChatGPT vs Lakshmi for LinkedIn posts — a detailed, honest comparison. Which AI tool actually sounds like you, scores output quality, and is built for Indian professionals?");
        return () => s.remove();
    }, []);

    return (
        <div className="min-h-screen">
            <Navbar />
            <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-20">

                {/* Hero */}
                <header className="mb-12">
                    <div className="lk-label mb-3">Tool Comparison · 7 min read</div>
                    <h1 className="font-heading text-3xl sm:text-5xl tracking-tight mb-6 leading-tight">
                        ChatGPT vs Lakshmi: which AI tool writes LinkedIn posts <span className="lk-gradient-text">in your voice?</span>
                    </h1>
                    <p className="text-[var(--lk-muted)] text-lg leading-relaxed">
                        ChatGPT can write a LinkedIn post. So can Lakshmi. But one of them sounds like generic "thought leadership" drivel — and the other sounds like you. Here's the honest comparison.
                    </p>
                </header>

                {/* Blog hero illustration */}
                <div className="mb-10 lk-border lk-surface rounded-2xl overflow-hidden">
                    <picture>
                        <source srcSet="/image/blog/chatgpt-vs-lakshmi.webp" type="image/webp" />
                        <img src="/image/blog/chatgpt-vs-lakshmi.png" alt="ChatGPT vs Lakshmi comparison"
                            className="w-full h-48 sm:h-64 object-cover" loading="lazy"
                            onError={e => { e.target.style.display = "none"; }} />
                    </picture>
                </div>

                {/* Character accent */}
                <div className="flex justify-center mb-10">
                    <picture>
                        <source srcSet="/image/lakshmi_nobg.webp" type="image/webp" />
                        <img src="/image/lakshmi_nobg_opt.png" alt="Lakshmi AI"
                            className="w-36 sm:w-44 lk-float" loading="lazy"
                            style={{ filter: "drop-shadow(0 0 24px rgba(124,58,237,0.4))" }} />
                    </picture>
                </div>

                {/* The core problem with ChatGPT */}
                <section className="mb-10">
                    <h2 className="font-heading text-2xl sm:text-3xl tracking-tight mb-4">The core problem with ChatGPT for LinkedIn</h2>
                    <p className="text-[var(--lk-muted)] leading-relaxed mb-4">
                        ChatGPT is a general-purpose language model. It can write code, explain quantum mechanics, and draft a wedding speech. That versatility is its strength — and its weakness for LinkedIn.
                    </p>
                    <p className="text-[var(--lk-muted)] leading-relaxed mb-4">
                        When you ask ChatGPT to "write a LinkedIn post about scaling my startup," you get something like: <em>"Scaling a startup is no small feat. It requires vision, determination, and the ability to pivot…"</em>
                    </p>
                    <p className="leading-relaxed font-medium">
                        Nobody on LinkedIn reads past line 2 of that. It's not wrong — it's just entirely forgettable.
                    </p>
                </section>

                {/* Side-by-side example */}
                <section className="mb-12">
                    <h2 className="font-heading text-2xl sm:text-3xl tracking-tight mb-6">Same idea — ChatGPT vs Lakshmi</h2>
                    <div className="lk-label mb-3">Input: "I scaled our API from 10K to 10M requests/day"</div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div className="lk-border lk-surface rounded-xl p-5">
                            <div className="text-sm font-semibold text-[var(--lk-muted)] mb-3 flex items-center gap-2">
                                <XCircle className="w-4 h-4 text-red-400" />
                                ChatGPT output
                            </div>
                            <p className="text-sm text-[var(--lk-muted)] italic leading-relaxed">
                                "Scaling infrastructure from 10K to 10M requests is a massive achievement! It requires careful planning, strong engineering practices, and a dedicated team. Here are some key lessons I learned…"
                            </p>
                            <div className="mt-3 px-2 py-1 rounded-full text-xs inline-block"
                                style={{ background: "rgba(239,68,68,0.15)", color: "#f87171" }}>
                                Generic · No hook · Won't stop the scroll
                            </div>
                        </div>
                        <div className="lk-border rounded-xl p-5" style={{ borderColor: "rgba(124,58,237,0.4)", background: "rgba(124,58,237,0.04)" }}>
                            <div className="text-sm font-semibold text-[var(--lk-primary)] mb-3 flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                                Lakshmi output
                            </div>
                            <p className="text-sm italic leading-relaxed">
                                "Our API hit 10M requests/day last Tuesday.<br /><br />
                                Nobody celebrated. We were all just relieved nothing had caught fire.<br /><br />
                                Getting from 10K to 10M wasn't a straight line. It was 3 major rewrites, 1 complete database migration, and 6 months of 2 AM pages…"
                            </p>
                            <div className="mt-3 px-2 py-1 rounded-full text-xs inline-block"
                                style={{ background: "rgba(52,211,153,0.15)", color: "#34D399" }}>
                                Score: 93/100 · Hook · Story · Voice matched
                            </div>
                        </div>
                    </div>
                </section>

                {/* Feature comparison table */}
                <section className="mb-12">
                    <h2 className="font-heading text-2xl sm:text-3xl tracking-tight mb-6">Feature comparison</h2>
                    <div className="lk-border lk-surface rounded-2xl overflow-hidden">
                        <div className="grid grid-cols-3 text-xs font-semibold text-[var(--lk-muted)] uppercase tracking-wider p-4 border-b border-[var(--lk-border)]">
                            <div>Feature</div>
                            <div className="text-center">ChatGPT</div>
                            <div className="text-center text-[var(--lk-primary)]">Lakshmi</div>
                        </div>
                        {COMPARISON.map((row, i) => (
                            <motion.div key={row.feature}
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                transition={{ delay: i * 0.04 }}
                                viewport={{ once: true }}
                                className={`grid grid-cols-3 p-4 text-sm ${i < COMPARISON.length - 1 ? "border-b border-[var(--lk-border)]" : ""}`}
                            >
                                <div className="text-[var(--lk-muted)]">{row.feature}</div>
                                <div className="flex justify-center">
                                    {row.chatgpt
                                        ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                                        : <XCircle className="w-4 h-4 text-red-400/60" />}
                                </div>
                                <div className="flex justify-center">
                                    {row.lakshmi
                                        ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                                        : <XCircle className="w-4 h-4 text-red-400/60" />}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </section>

                {/* When to use each */}
                <section className="mb-12">
                    <h2 className="font-heading text-2xl sm:text-3xl tracking-tight mb-5">The honest verdict</h2>
                    <div className="space-y-4">
                        <div className="lk-border lk-surface rounded-xl p-5">
                            <div className="font-semibold mb-2">Use ChatGPT when…</div>
                            <p className="text-sm text-[var(--lk-muted)]">You need general-purpose writing, code help, research, or want full control over a multi-step prompting process. ChatGPT is a powerful blank slate.</p>
                        </div>
                        <div className="lk-border rounded-xl p-5" style={{ borderColor: "rgba(124,58,237,0.4)", background: "rgba(124,58,237,0.04)" }}>
                            <div className="font-semibold text-[var(--lk-primary)] mb-2 flex items-center gap-2"><Zap className="w-4 h-4" />Use Lakshmi when…</div>
                            <p className="text-sm">You want to build your personal brand on LinkedIn and social media — consistently, in your voice, without spending hours writing. Lakshmi is purpose-built for this one job.</p>
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <div className="text-center lk-border lk-surface rounded-2xl p-8">
                    <h3 className="font-heading text-2xl tracking-tight mb-3">Try Lakshmi — it's free</h3>
                    <p className="text-[var(--lk-muted)] text-sm mb-6">Write your first LinkedIn post in under 60 seconds. No prompting needed. No credit card required.</p>
                    <Link to="/signup" className="lk-btn-primary rounded-xl px-8 py-4 inline-flex items-center gap-2 text-sm">
                        Try Lakshmi free <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </article>
        </div>
    );
}
